/**
 * Meta Pixel (Facebook Pixel) — client-side tracking + server-side CAPI relay.
 *
 * Client-side: loads the Meta Pixel script and fires standard events.
 * Server-side: mirrors key events to the Conversions API via an edge function
 * for better attribution (especially with iOS ATT / ad blockers).
 *
 * Parameters sent for maximum Event Match Quality:
 * - client_ip_address (from request headers, server-side)
 * - client_user_agent (from request headers, server-side)
 * - fbp (Browser ID cookie)
 * - fbc (Click ID cookie, with fbclid URL fallback)
 * - external_id (persistent anonymous UUID for cross-session matching)
 * - event_id (for pixel ↔ CAPI deduplication)
 */

import { hasOptedOut } from "./consent";
import { isProductionHost } from "./is-production-host";

const META_PIXEL_ID = "951669890667464";
// ─── fbq type shim ──────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

// ─── Persistent external_id (anonymous UUID) ────────────────────────
const EXTERNAL_ID_KEY = "oaria_ext_id";

function getExternalId(): string {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(EXTERNAL_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(EXTERNAL_ID_KEY, id);
  }
  return id;
}

// ─── Pixel base code (runs once) ────────────────────────────────────
let pixelLoaded = false;

export function initMetaPixel() {
  if (pixelLoaded || typeof window === "undefined" || !isProductionHost()) return;
  pixelLoaded = true;

  /* Meta's standard base code — minified inline to avoid extra network round-trips */
  (function (f: Window, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function (...args: unknown[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  // Honor US opt-out (CCPA/CPRA) via Limited Data Use mode.
  // 0/0 = let Meta geolocate; applies LDU to any US state that requires it.
  if (hasOptedOut()) {
    window.fbq("dataProcessingOptions", ["LDU"], 0, 0);
  } else {
    window.fbq("dataProcessingOptions", []);
  }

  // Init with external_id for advanced matching
  window.fbq("init", META_PIXEL_ID, { external_id: getExternalId() });
  window.fbq("track", "PageView");

  // Store fbclid from URL into _fbc cookie if not already set
  storeFbcFromUrl();
}

// ─── fbclid → _fbc cookie helper ────────────────────────────────────
function storeFbcFromUrl() {
  try {
    const url = new URL(window.location.href);
    const fbclid = url.searchParams.get("fbclid");
    if (fbclid && !getCookie("_fbc")) {
      // Format: fb.1.{timestamp}.{fbclid}
      const fbc = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${fbc};max-age=${90 * 86400};path=/;SameSite=Lax`;
    }
  } catch {
    /* ignore */
  }
}

// ─── Client-side standard events ────────────────────────────────────
export function trackPageView() {
  const eventId = crypto.randomUUID();
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "PageView", {}, { eventID: eventId });
  }
  sendCapi("PageView", {}, eventId);
}

export function trackViewContent(params: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value?: number;
  currency?: string;
}) {
  const eventId = crypto.randomUUID();
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "ViewContent", params, { eventID: eventId });
  }
  sendCapi("ViewContent", params, eventId);
}

export function trackAddToCart(params: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
}) {
  const eventId = crypto.randomUUID();
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "AddToCart", params, { eventID: eventId });
  }
  sendCapi("AddToCart", params, eventId);
}

export function trackInitiateCheckout(params: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
}) {
  const eventId = crypto.randomUUID();
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "InitiateCheckout", params, { eventID: eventId });
  }
  sendCapi("InitiateCheckout", params, eventId);
}

// ─── Server-side CAPI relay (fire & forget) ─────────────────────────
function sendCapi(eventName: string, params: Record<string, unknown> = {}, eventId: string) {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  if (!SUPABASE_URL || !isProductionHost()) return;

  const optedOut = hasOptedOut();
  const url = `${SUPABASE_URL}/functions/v1/meta-capi`;
  const payload = JSON.stringify({
    event_name: eventName,
    event_id: eventId,
    event_source_url: window.location.href,
    custom_data: params,
    // Drop user-level identifiers when the visitor has opted out.
    fbc: optedOut ? undefined : getCookie("_fbc") || undefined,
    fbp: optedOut ? undefined : getCookie("_fbp") || undefined,
    external_id: optedOut ? undefined : getExternalId() || undefined,
    limited_data_use: optedOut,
  });

  // Guard against double-sending a given event_id
  let sent = false;

  function fire() {
    if (sent) return;
    sent = true;
    // fetch with keepalive survives page unload (redirect to checkout)
    // while keeping normal CORS behaviour. sendBeacon with a JSON Blob
    // triggers a preflight that the browser then refuses to follow,
    // killing the POST entirely. keepalive avoids that — same CORS path
    // as a regular fetch, 64KB payload limit (well above our ~260 bytes).
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  }

  // Send immediately when _fbp is already set (common case).
  // Only delay when _fbp is missing (first-time visitor, cookie not yet set).
  if (getCookie("_fbp")) {
    fire();
  } else {
    const timer = setTimeout(fire, 1500);
    // If the page starts unloading before the timer fires (e.g. redirect to
    // checkout), fire immediately and cancel the timer to avoid double-send.
    function onHidden() {
      clearTimeout(timer);
      fire();
    }
    window.addEventListener("pagehide", onHidden, { once: true });
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}
